import { useState } from 'react';
import styled from 'styled-components';
import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { theme } from '../../../../styles/theme';
import { getScrollBarStyles } from '../../../../config/styles';
import { IDisplayedSlice } from '../../interfaces';
import { floatingComponentStyles } from '../../../../styles/shareableStyles';
import { IAnalysisColorConfig } from '../../utils/analysisColorConfig';
import { StyledSingleSelectOption } from '../../../Dropdown/SingleSelect';
import CompactChartGroupItemIcon from './CompactChartGroupItemIcon';
import KubitTooltip from '../../../KubitTooltip';
import { generateGroupbyHint } from '../../utils';
import { TextButton } from '../../../Button';
import { FloatingLayer } from '../../../FloatingManager';

interface IProps {
  label: string;
  displayedSlices: IDisplayedSlice[];
  onToggleSlice: (sliceKey: string, isVisible: boolean) => void;
  colorConfig: IAnalysisColorConfig;
}

const MenuWrapper = styled.div`
  min-width: 120px;
  max-height: 200px;
  ${floatingComponentStyles};
  overflow-y: auto;
  ${getScrollBarStyles(2)};
`;
const StyledLabel = styled(TextButton)`
  align-self: center;
  font-weight: 500;
  &[aria-describedby] {
    color: ${theme.colors.mainBlue};
  }
`;

const CompactChartGroupMenu = ({
  label,
  displayedSlices,
  onToggleSlice,
  colorConfig,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip()],
    strategy: 'fixed',
  });

  const hover = useHover(context, {
    // allow hovering inside of tooltip
    handleClose: safePolygon(),
    move: false,
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return (
    <>
      <StyledLabel ref={refs.setReference} {...getReferenceProps()}>
        {label}
      </StyledLabel>
      {isOpen && (
        <FloatingLayer>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <MenuWrapper>
              {displayedSlices.map((slice) => (
                <KubitTooltip
                  key={slice.key}
                  disabled={!slice.hint || slice.hint.length === 0}
                  content={generateGroupbyHint(slice.hint, slice.rank)}
                  placement="left"
                >
                  <StyledSingleSelectOption
                    onClick={() => onToggleSlice(slice.key, slice.isVisible)}
                    isActive={slice.isVisible}
                  >
                    <CompactChartGroupItemIcon
                      slice={slice}
                      colorConfig={colorConfig}
                    />
                    {slice.name}
                  </StyledSingleSelectOption>
                </KubitTooltip>
              ))}
            </MenuWrapper>
          </div>
        </FloatingLayer>
      )}
    </>
  );
};

export default CompactChartGroupMenu;
